import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Card, CardContent, Button } from "@kmf/ui";
import { noticeApi, type Notice } from "@/lib/api";

const ITEMS_PER_PAGE = 10;

export function NoticeListPage() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    document.title = "공지사항 - KMF 할랄위원회";
    loadNotices();
  }, []);

  const loadNotices = async () => {
    try {
      const data = await noticeApi.list();
      setNotices(data);
    } catch (error) {
      console.error("Failed to load notices:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(notices.length / ITEMS_PER_PAGE);
  const paginatedNotices = notices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="public-page py-12 md:py-16">
      <Container>
        {/* Page Header */}
        <div className="public-page-header">
          <h1 className="text-3xl font-bold text-foreground">공지사항</h1>
          <p className="mt-2 text-muted-foreground">
            KMF 할랄위원회의 공지사항을 확인하세요
          </p>
        </div>

        {/* Notice List */}
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            로딩 중...
          </div>
        ) : notices.length === 0 ? (
          <Card className="public-panel">
            <CardContent className="py-12 text-center text-muted-foreground">
              등록된 공지사항이 없습니다.
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="publication-list space-y-3">
              {paginatedNotices.map((notice) => (
                <Link
                  key={notice.NoticeID}
                  to={`/notices/${notice.NoticeID}`}
                  className="block"
                >
                  <Card className="public-panel transition-colors hover:bg-accent">
                    <CardContent className="p-4 md:p-6">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div className="flex-1">
                          <span className="publication-type inline-block rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            공지사항
                          </span>
                          <h2 className="mt-2 font-medium text-foreground line-clamp-1">
                            {notice.Title}
                          </h2>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {notice.UpdateDateTime}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination mt-8 flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  이전
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  )
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  다음
                </Button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
}
